import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { AdminHeader } from "./adminShared";
import { Search, Loader2, Trash2, Ban, CheckCircle2 } from "lucide-react";

export default function AdminOrganizers() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/admin/organizers?search=${search}`).then(({ data }) => setRows(data.organizers)).finally(() => setLoading(false));
  }, [search]);
  useEffect(() => { load(); }, [load]);

  const suspend = async (o) => {
    const action = o.suspended ? "reactivate" : "suspend";
    if (!window.confirm(`${o.suspended ? "Reactivate" : "Suspend"} "${o.organization_name}"?\n\n${o.suspended ? "They will be able to log in and manage races again." : "They will be blocked from logging in, but all their races and data are kept."}`)) return;
    try {
      const { data } = await api.post(`/admin/organizers/${o.id}/toggle`);
      toast.success(data.suspended ? "Organizer suspended" : "Organizer reactivated");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || `Could not ${action} organizer`); }
  };

  const setBilling = async (o, bc) => {
    try { await api.put(`/admin/organizers/${o.id}/billing`, { billing_country: bc }); toast.success(`Billing country set to ${bc}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update billing country"); }
  };

  const del = async (o) => {
    if (!window.confirm(`Delete organizer "${o.organization_name}"?\n\nThis permanently removes the organizer account plus their ${o.race_count} race(s) and all related payments. This cannot be undone.`)) return;
    try {
      const { data } = await api.delete(`/admin/organizers/${o.id}`);
      toast.success(`Organizer deleted (${data.races_deleted} race(s) removed)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not delete organizer"); }
  };

  return (
    <div>
      <AdminHeader title="Organizers" subtitle="Everyone who lists races on YourRaceFinder." />
      <div className="relative max-w-sm mb-4"><Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search organizers..." className="w-full border border-slate-300 rounded-lg pl-9 pr-3 py-2 text-sm outline-none focus:border-[#22d3ee]" data-testid="admin-organizers-search" /></div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto" data-testid="admin-organizers-table">
        {loading ? <div className="py-12 grid place-items-center"><Loader2 className="animate-spin text-[#22d3ee]" /></div> :
        <table className="w-full text-sm"><thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr><th className="text-left p-4">Organization</th><th className="text-left p-4">Email</th><th className="text-left p-4">Phone</th><th className="text-right p-4">Races</th><th className="text-right p-4">Published</th><th className="text-right p-4">Paid</th><th className="text-left p-4">Billing</th><th className="text-right p-4">Actions</th></tr></thead>
          <tbody>{rows.map((o) => (
            <tr key={o.id} className="border-t border-slate-100" data-testid={`organizer-row-${o.id}`}>
              <td className="p-4 font-semibold text-slate-800">
                <div className="flex items-center gap-2">{o.organization_name}
                  {o.suspended && <span className="text-[10px] font-bold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded" data-testid={`organizer-suspended-badge-${o.id}`}>SUSPENDED</span>}
                </div>
              </td>
              <td className="p-4 text-slate-600">{o.email}</td>
              <td className="p-4 text-slate-600">{o.phone || "—"}</td>
              <td className="p-4 text-right">{o.race_count}</td>
              <td className="p-4 text-right">{o.published_count}</td>
              <td className="p-4 text-right">{o.paid_count}</td>
              <td className="p-4">
                <select value={o.billing_country || "MY"} onChange={(e) => setBilling(o, e.target.value)} data-testid={`org-billing-${o.id}`} className="border border-slate-300 rounded-lg px-2 py-1 text-sm outline-none focus:border-[#22d3ee]">
                  <option value="MY">Malaysia</option><option value="SG">Singapore</option>
                </select>
              </td>
              <td className="p-4 text-right">
                <div className="inline-flex items-center gap-1.5">
                  <button onClick={() => suspend(o)} className={`p-2 border rounded-lg inline-flex ${o.suspended ? "border-emerald-300 text-emerald-600 hover:bg-emerald-50" : "border-slate-300 text-slate-600 hover:bg-amber-50 hover:text-amber-600"}`} title={o.suspended ? "Reactivate organizer" : "Suspend organizer"} data-testid={`suspend-organizer-${o.id}`}>{o.suspended ? <CheckCircle2 size={15} /> : <Ban size={15} />}</button>
                  <button onClick={() => del(o)} className="p-2 border border-slate-300 rounded-lg hover:bg-red-50 hover:text-red-500 inline-flex" title="Delete organizer and all their races" data-testid={`delete-organizer-${o.id}`}><Trash2 size={15} /></button>
                </div>
              </td>
            </tr>
          ))}</tbody>
        </table>}
        {!loading && rows.length === 0 && <p className="p-8 text-center text-slate-500">No organizers found.</p>}
      </div>
    </div>
  );
}
